import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import BrandLogo from '@/components/BrandLogo';
import PublicHeader from '@/components/PublicHeader';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Building2, 
  Users, 
  Bell, 
  CheckCircle2, 
  ArrowRight,
  Shield,
  Clock,
  BarChart3
} from 'lucide-react';
import { getStoredUser } from '@/lib/auth';
import { useEffect } from 'react';

export default function LandingPage() {
  const navigate = useNavigate();
  
  useEffect(() => {
    // Redirect if already logged in
    const user = getStoredUser();
    if (user) {
      navigate(user.role === 'employer' ? '/employer' : '/toc');
    }
  }, [navigate]);
  
  const features = [
    {
      icon: Building2,
      title: 'For Schools',
      description: 'Post positions to your Sub list instantly. Monitor responses in real-time and fill positions faster.'
    },
    {
      icon: Users,
      title: 'For Substitute Teachers',
      description: 'Get instant alerts for new positions. Accept or decline with one tap. Never miss an opportunity.'
    },
    {
      icon: Bell,
      title: 'Real-time Notifications',
      description: 'Audio alerts ensure you never miss a posting, even when your phone is on silent.'
    },
    {
      icon: CheckCircle2,
      title: 'Quick Response',
      description: 'Simple accept/decline buttons. First to accept gets the position.'
    },
    {
      icon: Shield,
      title: 'Secure Connection',
      description: 'Schools share a unique code with their trusted Subs. Only connected Subs see your postings.'
    },
    {
      icon: BarChart3,
      title: 'Analytics & Tracking',
      description: 'Schools track Sub behavior. Subs see their history with each school.'
    }
  ];
  
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <PublicHeader testIdPrefix="header" logoTestId="header-home-link" />
      
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-left">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight font-['Manrope'] leading-tight">
                Fill substitute positions{' '}
                <span className="text-emerald-400">in minutes</span>
              </h1>
              <p className="mt-6 text-lg text-slate-300 max-w-xl">
                Connecting schools and their trusted substitute teachers. Post positions, get instant responses, 
                and fill vacancies before the school day starts.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-8"
                  onClick={() => navigate('/register?role=employer')}
                  data-testid="hero-employer-btn"
                >
                  I'm a School Admin
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-slate-600 text-white hover:bg-slate-800"
                  onClick={() => navigate('/register?role=toc')}
                  data-testid="hero-toc-btn"
                >
                  I'm a Substitute Teacher
                </Button>
              </div>
            </div>
            <div className="hidden md:block">
              <img 
                src="/hero/teacher-morning.webp"
                alt="Substitute teacher checking ExpressSub for morning positions"
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover object-center"
              />
            </div>
          </div>
        </div>
      </section>
      
      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-left mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-['Manrope']">
              How It Works
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Simple, fast, and effective for both schools and substitutes
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12">
            {/* For Schools */}
            <Card className="border-2 border-slate-200 rounded-xl">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-12 w-12 rounded-lg bg-slate-900 flex items-center justify-center">
                    <Building2 className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 font-['Manrope']">For Schools</h3>
                </div>
                <ol className="space-y-4">
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 h-6 w-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-sm font-semibold">1</span>
                    <span className="text-slate-600">Create your school profile with default settings</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 h-6 w-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-sm font-semibold">2</span>
                    <span className="text-slate-600">Share your unique school code with trusted Subs</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 h-6 w-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-sm font-semibold">3</span>
                    <span className="text-slate-600">Post positions with pre-filled defaults</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 h-6 w-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-sm font-semibold">4</span>
                    <span className="text-slate-600">Monitor responses and track analytics</span>
                  </li>
                </ol>
              </CardContent>
            </Card>
            
            {/* For TOCs */}
            <Card className="border-2 border-slate-200 rounded-xl">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-12 w-12 rounded-lg bg-emerald-600 flex items-center justify-center">
                    <Users className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 font-['Manrope']">For Substitute Teachers</h3>
                </div>
                <ol className="space-y-4">
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 h-6 w-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-sm font-semibold">1</span>
                    <span className="text-slate-600">Create your Sub account</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 h-6 w-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-sm font-semibold">2</span>
                    <span className="text-slate-600">Enter school codes to connect with schools</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 h-6 w-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-sm font-semibold">3</span>
                    <span className="text-slate-600">Receive instant alerts for new positions</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 h-6 w-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-sm font-semibold">4</span>
                    <span className="text-slate-600">Accept positions with one tap</span>
                  </li>
                </ol>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      
      {/* Features Grid */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-left mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-['Manrope']">
              Everything You Need
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Powerful features for efficient substitute management
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => (
              <Card key={feature.title} className="border border-slate-200 rounded-xl hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="h-12 w-12 rounded-lg bg-slate-100 flex items-center justify-center mb-4">
                    <feature.icon className="h-6 w-6 text-slate-700" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 font-['Manrope'] mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 text-sm">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold font-['Manrope'] mb-6">
            Ready to simplify substitute management?
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Join schools and substitute teachers who are already saving time every day.
          </p>
          <Button 
            size="lg"
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-8"
            onClick={() => navigate('/register')}
            data-testid="cta-get-started-btn"
          >
            Get Started Free
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <BrandLogo variant="white" size="md" />
            </div>
            <div className="flex items-center gap-4 text-sm">
              <Link to="/pricing" className="hover:text-white transition-colors" data-testid="footer-pricing-link">Pricing</Link>
              <span className="text-slate-600">|</span>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
              <span className="text-slate-600">|</span>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <span className="text-slate-600">|</span>
              <Link to="/contact" className="hover:text-white transition-colors" data-testid="footer-contact-link">Contact Us</Link>
            </div>
            <p className="text-sm">
              © 2026 ExpressSub. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
